import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, type RouteObject } from "react-router";
import { RedirectIfAuthed, RequireAuth, RequireSetup } from "./guards";
import { FullPageLoader } from "@/components/FullPageLoader";
import { AppShell } from "./shell/AppShell";
import { AuthShell } from "./shell/AuthShell";
import { RootLayout } from "./shell/RootLayout";
import { RouteError } from "@/components/RouteError";

const page = (
  loader: () => Promise<{ default: React.ComponentType }>,
): ReactNode => {
  const C = lazy(loader);
  return (
    <Suspense fallback={<FullPageLoader />}>
      <C />
    </Suspense>
  );
};

export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: "/auth",
        element: <RedirectIfAuthed />,
        errorElement: <RouteError />,
        children: [
          {
            element: <AuthShell />,
            children: [
              {
                index: true,
                element: page(() => import("@/features/auth/LoginPage")),
              },
              {
                path: "garmin",
                element: page(() => import("@/features/auth/GarminAuthPage")),
              },
            ],
          },
        ],
      },
      { path: "/applogin", element: <Navigate to="/auth" replace /> },
      // The iOS app's universal-link namespace (#381): normally iOS opens the
      // app; in a browser this is the tap-to-open fallback.
      { path: "/app/*", element: page(() => import("@/features/auth/AppLinkPage")) },
      {
        element: <AppShell />,
        errorElement: <RouteError />,
        children: [
          // Public
          {
            path: "/viewactivity/:id",
            element: page(
              () => import("@/features/activity-detail/ActivityDetailPage"),
            ),
          },
          {
            path: "/about",
            element: page(() => import("@/features/about/AboutPage")),
          },
          {
            path: "/privacy",
            element: page(() => import("@/features/about/PrivacyPage")),
          },
          {
            path: "/terms",
            element: page(() => import("@/features/about/TermsPage")),
          },
          {
            path: "/pacecalc",
            element: page(
              () => import("@/features/calculator/PaceCalculatorPage"),
            ),
          },
          // Authenticated
          {
            element: <RequireAuth />,
            children: [
              {
                path: "/onboarding/account",
                element: page(
                  () => import("@/features/onboarding/AccountStepPage"),
                ),
              },
              {
                path: "/onboarding/synch",
                element: page(
                  () => import("@/features/onboarding/SynchStepPage"),
                ),
              },
              {
                path: "/connect",
                element: page(
                  () => import("@/features/onboarding/GarminConnectPage"),
                ),
              },
              {
                path: "/upload",
                element: page(() => import("@/features/upload/UploadPage")),
              },
              {
                path: "/connected-ai",
                element: page(
                  () => import("@/features/account/ConnectedAiPage"),
                ),
              },
              {
                path: "/account",
                element: page(() => import("@/features/account/AccountPage")),
              },
              {
                path: "/oauth/consent",
                element: page(() => import("@/features/oauth/ConsentPage")),
              },
              {
                path: "/devices",
                element: page(() => import("@/features/tv/DevicesPage")),
              },
              {
                path: "/devices/setup",
                element: page(() => import("@/features/tv/RunClockSetupPage")),
              },
              {
                path: "/impersonate",
                element: page(() => import("@/features/auth/ImpersonatePage")),
              },
              {
                element: <RequireSetup />,
                children: [
                  { path: "/", element: <Navigate to="/home" replace /> },
                  {
                    path: "/home",
                    element: page(() => import("@/features/home/HomePage")),
                  },
                  {
                    path: "/runs",
                    element: page(
                      () => import("@/features/activities/ActivitiesPage"),
                    ),
                  },
                  {
                    path: "/runs/:id",
                    element: page(
                      () =>
                        import("@/features/activity-detail/ActivityDetailPage"),
                    ),
                  },
                  {
                    path: "/pb",
                    element: page(
                      () => import("@/features/pbs/PersonalBestsPage"),
                    ),
                  },
                  {
                    path: "/pbtable",
                    element: page(() => import("@/features/pbs/PbTablePage")),
                  },
                  {
                    path: "/stats",
                    element: page(() => import("@/features/stats/StatsPage")),
                  },
                  {
                    path: "/annual",
                    element: page(
                      () => import("@/features/stats/AnnualSummariesPage"),
                    ),
                  },
                  // Old Quasar paths (bookmarks, push-notification deep links)
                  { path: "/start", element: <Navigate to="/home" replace /> },
                  {
                    path: "/dashboard",
                    element: <Navigate to="/home" replace />,
                  },
                  {
                    path: "/challenges",
                    element: <Navigate to="/stats" replace />,
                  },
                  {
                    path: "/personalbest",
                    element: <Navigate to="/pb" replace />,
                  },
                  {
                    path: "/synch",
                    element: <Navigate to="/onboarding/synch" replace />,
                  },
                  {
                    path: "/setup",
                    element: <Navigate to="/account" replace />,
                  },
                ],
              },
            ],
          },
          {
            path: "*",
            element: page(() => import("@/features/errors/NotFoundPage")),
          },
        ],
      },
    ],
  },
];
